import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  OPS_BACKLOG_BUG,
  OPS_BUG_CAUSE_CLASS_FIELD,
  OPS_BUG_DISCOVERY_CHANNEL_FIELD,
  OPS_BUG_INCIDENT_LEVEL_FIELD,
  OPS_BUG_REPO_FIELD,
  OPS_BUG_RESPONSIBLE_META,
  OPS_BUG_SECTION_FIELDS,
  emptyOpsBugMeta,
  emptyOpsBugSections,
} from '@cortex/ops-core';
import type { OpsBugFieldOption } from '@cortex/ops-core';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Input } from '@cortex/web-design-system/components/ui/Input';
import { Textarea } from '@cortex/web-design-system/components/ui/Textarea';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import {
  useCreateOpsBugTicket,
  useDraftOpsBugTicket,
  useOpsSimilar,
} from '../../../hooks/useOps.js';
import type {
  OpsBugTicketFieldKey,
  OpsBugTicketMeta,
  OpsBugTicketSections,
} from '../../../types/ops.js';

/** リスト選択肢を design-system Select の {value,label} に変換する。 @graph-connects none */
function toOptions(options: readonly OpsBugFieldOption[]): { value: string; label: string }[] {
  return options.map((o) => ({ value: String(o.id), label: o.label }));
}

/** 空文字を除き、出現順を保って重複を排除する。 @graph-connects none */
function dedupeKeys(keys: readonly string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const key of keys) {
    if (key !== '' && !seen.has(key)) {
      seen.add(key);
      result.push(key);
    }
  }
  return result;
}

/** メタのセレクト対応キー（数値 option id を持つフィールド）。 @graph-connects none */
type MetaSelectKey =
  | 'urgencyOptionId'
  | 'discoveryChannelOptionId'
  | 'incidentLevelOptionId'
  | 'causeClassOptionId'
  | 'repoOptionId'
  | 'responsibleDivOptionId'
  | 'responsibleGroupOptionId'
  | 'responsibleTeamOptionId'
  | 'businessOptionId';

/** メタのセレクト定義（option id フィールド）。ラベル・選択肢・必須・ハイライトキーを 1 箇所に。 @graph-connects none */
interface MetaSelectDef {
  metaKey: MetaSelectKey;
  reviewKey: OpsBugTicketFieldKey;
  options: { value: string; label: string }[];
  required: boolean;
}

/** Responsible 4 種は ops-core の OPS_BUG_RESPONSIBLE_META を単一 source に（API の起因案件補完とキー対応を共有）。 @graph-connects none */
const RESPONSIBLE_SELECTS: MetaSelectDef[] = OPS_BUG_RESPONSIBLE_META.map((m) => ({
  metaKey: m.metaKey,
  reviewKey: m.reviewKey,
  options: toOptions(m.field.options),
  required: true,
}));

/** 全メタセレクトの表示順（緊急度 → 発見の経路（202610新運用）→ Responsible 4 種 → 任意メタ）。 @graph-connects none */
const META_SELECTS: MetaSelectDef[] = [
  {
    metaKey: 'urgencyOptionId',
    reviewKey: 'urgency',
    options: toOptions(OPS_BACKLOG_BUG.requiredCustomFields[0].options),
    required: true,
  },
  {
    metaKey: 'discoveryChannelOptionId',
    reviewKey: 'discoveryChannel',
    options: toOptions(OPS_BUG_DISCOVERY_CHANNEL_FIELD.options),
    required: true,
  },
  ...RESPONSIBLE_SELECTS,
  {
    metaKey: 'incidentLevelOptionId',
    reviewKey: 'incidentLevel',
    options: toOptions(OPS_BUG_INCIDENT_LEVEL_FIELD.options),
    required: false,
  },
  {
    metaKey: 'causeClassOptionId',
    reviewKey: 'causeClass',
    options: toOptions(OPS_BUG_CAUSE_CLASS_FIELD.options),
    required: false,
  },
  {
    metaKey: 'repoOptionId',
    reviewKey: 'repo',
    options: toOptions(OPS_BUG_REPO_FIELD.options),
    required: false,
  },
];

/** @graph-connects none */
export interface OpsBugTicketModalProps {
  /** 対象 ops 依頼 id（slug:No）。 */
  requestId: string;
  /** 類似バグスキャンのもとになる本文（背景 + 対応依頼）。起因案件候補の取得に使う。 */
  requestText: string;
  /**
   * 依頼に既に紐付いている参考バグ（relatedTickets の target==='backlog'）の issueKey。ライブの類似バグ
   * スキャンと合わせて起因案件候補として提示する（重複排除）。サーバーも起因案件セットへ自動マージする。
   */
  linkedBugKeys: string[];
  /** モーダルの開閉。 */
  open: boolean;
  /** 閉じる。 */
  onClose: () => void;
}

/**
 * ops 依頼詳細からのバグ起票モーダル（Level 1 = AI ドラフト → 人手確認 → 起票）。開くと AI 下書きを取得し、
 * Backlog の各構造化フィールド（タイトル / セクション本文 / バグ緊急度・インシデント・原因分類・起因リポジトリ・
 * 影響人数・Responsible 組織 / 起因案件 / 参考リンク）をプリフィルする。AI が埋めきれなかった箇所（needsReview）と
 * 必須未入力はハイライトし、Proceed で初めて Backlog に起票する。起票成功でチケット URL を表示する。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business 依頼詳細のバグ起票モーダル。AI 下書き（タイトル/セクション本文/緊急度・発見の経路（202610新運用。既定は CS / Opetan）・インシデント・原因分類・起因リポジトリ・影響人数・Responsible 組織/起因案件/参考リンク）を人手レビュー・編集し、Proceed で Backlog に構造化起票する。要確認箇所と必須未入力をハイライト、起因案件候補は類似バグスキャンから提示
 * @graph-connects useDraftOpsBugTicket [calls] モーダルを開いた時にバグチケット下書きを AI 生成する
 * @graph-connects useOpsSimilar [calls] 起因案件候補として類似バグチケットを取得する
 * @graph-connects useCreateOpsBugTicket [calls] Proceed で Backlog にバグチケットを起票する
 */
export function OpsBugTicketModal({
  requestId,
  requestText,
  linkedBugKeys,
  open,
  onClose,
}: OpsBugTicketModalProps): React.ReactElement {
  const { t } = useTranslation();
  const similar = useOpsSimilar(requestText, open);
  const draft = useDraftOpsBugTicket();
  const create = useCreateOpsBugTicket();
  // effect の依存が毎レンダー変わって再描画ループになるのを防ぐため、参照が安定した値だけを依存に使う。
  const { mutate: draftMutate, isPending: draftPending, isError: draftError } = draft;
  const { reset: createReset } = create;

  const [title, setTitle] = useState('');
  const [sections, setSections] = useState<OpsBugTicketSections>(emptyOpsBugSections);
  const [references, setReferences] = useState('');
  const [meta, setMeta] = useState<OpsBugTicketMeta>(emptyOpsBugMeta);
  const [needsReview, setNeedsReview] = useState<OpsBugTicketFieldKey[]>([]);
  const [drafted, setDrafted] = useState(false);

  const similarBugKeys = useMemo(
    () => similar.data?.bugs.map((b) => b.issueKey) ?? [],
    [similar.data],
  );
  // 起因案件候補は「依頼に既に紐付いた参考バグ」+「ライブの類似バグスキャン」を重複排除して並べる
  // （紐付け済みを先に）。サーバーも起因案件セットへ自動マージするが、UI でも選べるように提示する。
  // 候補生成は軽い純計算なので毎レンダー実行してよい（memo 不要）。
  const causalCandidateKeys = dedupeKeys([...linkedBugKeys, ...similarBugKeys]);
  // 類似バグスキャンが解決（成功 or 失敗）してから下書きを取得する。同一コミットで open が true になった直後は
  // similar がまだ pending で similarBugKeys が空のため、待たずに叩くと起因案件候補を AI に渡せない。
  const similarSettled = similar.isSuccess || similar.isError;

  // モーダルを開いたら 1 度だけ下書きを取得する。閉じたら状態をリセットする（次回開いたとき再取得）。
  useEffect(() => {
    if (!open) {
      setDrafted(false);
      setTitle('');
      setSections(emptyOpsBugSections());
      setReferences('');
      setMeta(emptyOpsBugMeta());
      setNeedsReview([]);
      createReset();
      return;
    }
    if (drafted || draftPending || !similarSettled) return;
    setDrafted(true);
    draftMutate(
      { id: requestId, similarBugKeys },
      {
        onSuccess: (d) => {
          setTitle(d.title);
          setSections(d.sections);
          setReferences(d.references);
          setMeta(d.meta);
          setNeedsReview(d.needsReview);
        },
      },
    );
  }, [
    open,
    drafted,
    draftPending,
    similarSettled,
    draftMutate,
    createReset,
    requestId,
    similarBugKeys,
  ]);

  const created = create.data;
  const orgFilled =
    meta.responsibleDivOptionId !== null &&
    meta.responsibleGroupOptionId !== null &&
    meta.responsibleTeamOptionId !== null &&
    meta.businessOptionId !== null;
  const canProceed =
    title.trim() !== '' &&
    sections.summary.trim() !== '' &&
    meta.urgencyOptionId !== null &&
    meta.discoveryChannelOptionId !== null &&
    orgFilled;

  const needsHint = t('ops.detail.bugTicket.needsReview');
  const sectionError = (key: keyof OpsBugTicketSections, required: boolean): string | undefined =>
    needsReview.includes(key) || (required && sections[key].trim() === '') ? needsHint : undefined;
  const selectError = (
    metaKey: MetaSelectKey,
    reviewKey: OpsBugTicketFieldKey,
    required: boolean,
  ): string | undefined =>
    needsReview.includes(reviewKey) || (required && meta[metaKey] === null) ? needsHint : undefined;

  // ユーザーが該当フィールドを補完したら「要確認」ハイライトを外す（Proceed まで赤枠が残らないように）。
  const dismissReview = (key: OpsBugTicketFieldKey): void => {
    setNeedsReview((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : prev));
  };

  const setSection = (key: keyof OpsBugTicketSections, value: string): void => {
    setSections((prev) => ({ ...prev, [key]: value }));
    dismissReview(key);
  };
  const setMetaOption = (
    key: MetaSelectKey,
    reviewKey: OpsBugTicketFieldKey,
    value: string,
  ): void => {
    setMeta(
      (prev) => ({ ...prev, [key]: value === '' ? null : Number(value) }) as OpsBugTicketMeta,
    );
    dismissReview(reviewKey);
  };

  const proceed = (): void => {
    const trimmed = { ...sections } as OpsBugTicketSections;
    for (const field of OPS_BUG_SECTION_FIELDS) trimmed[field.key] = sections[field.key].trim();
    create.mutate({
      id: requestId,
      input: {
        title: title.trim(),
        sections: trimmed,
        references: references.trim(),
        meta: {
          ...meta,
          causalIssueKey: meta.causalIssueKey?.trim() ? meta.causalIssueKey.trim() : null,
        },
      },
    });
  };

  return (
    <Modal open={open} onClose={onClose} title={t('ops.detail.bugTicket.title')}>
      {created ? (
        <div className="space-y-3">
          <p className="text-sm text-[var(--color-success)]">{t('ops.detail.bugTicket.created')}</p>
          <a
            href={created.url}
            target="_blank"
            rel="noreferrer"
            className="text-sm font-semibold text-[var(--color-accent-primary)] underline"
          >
            {created.issueKey}
          </a>
          <div className="flex justify-end">
            <Button type="button" variant="primary" size="sm" onClick={onClose}>
              {t('ops.detail.bugTicket.close')}
            </Button>
          </div>
        </div>
      ) : draftPending ? (
        <p className="text-sm text-[var(--color-text-secondary)]">
          {t('ops.detail.bugTicket.drafting')}
        </p>
      ) : draftError ? (
        <div className="space-y-3">
          <p className="text-sm text-[var(--color-error)]">
            {t('ops.detail.bugTicket.draftFailed')}
          </p>
          <div className="flex justify-end">
            <Button type="button" variant="secondary" size="sm" onClick={onClose}>
              {t('ops.detail.bugTicket.close')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
          {needsReview.length > 0 && (
            <p className="rounded-md bg-[var(--color-warning)]/10 px-3 py-2 text-xs text-[var(--color-warning)]">
              {t('ops.detail.bugTicket.missingHint')}
            </p>
          )}
          <Input
            label={`${t('ops.detail.bugTicket.fields.title')} *`}
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              dismissReview('title');
            }}
            error={needsReview.includes('title') ? needsHint : undefined}
          />

          {/* メタ（ドロップダウン。AI プリフィル済み。* は起票必須） */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {META_SELECTS.map(({ metaKey, reviewKey, options, required }) => (
              <Select
                key={metaKey}
                label={`${t(`ops.detail.bugTicket.meta.${reviewKey}`)}${required ? ' *' : ''}`}
                options={options}
                value={meta[metaKey] === null ? '' : String(meta[metaKey])}
                placeholder={t('ops.detail.bugTicket.selectPlaceholder')}
                onChange={(e) => {
                  setMetaOption(metaKey, reviewKey, e.target.value);
                }}
                error={selectError(metaKey, reviewKey, required)}
              />
            ))}
            <Input
              type="number"
              label={t('ops.detail.bugTicket.meta.affectedUserCount')}
              value={meta.affectedUserCount === null ? '' : String(meta.affectedUserCount)}
              onChange={(e) => {
                setMeta((prev) => ({
                  ...prev,
                  affectedUserCount: e.target.value === '' ? null : Number(e.target.value),
                }));
                dismissReview('affectedUserCount');
              }}
            />
          </div>

          {/* 起因案件（設定すると案件起因バグ + PL 補完。類似バグから選べる） */}
          <div className="space-y-1">
            <Input
              label={t('ops.detail.bugTicket.fields.causal')}
              value={meta.causalIssueKey ?? ''}
              placeholder="AIRCLOSET-1234"
              onChange={(e) => {
                setMeta((prev) => ({
                  ...prev,
                  causalIssueKey: e.target.value === '' ? null : e.target.value,
                }));
                dismissReview('causalTicket');
              }}
              error={needsReview.includes('causalTicket') ? needsHint : undefined}
            />
            {causalCandidateKeys.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {causalCandidateKeys.map((key) => (
                  <button
                    key={key}
                    type="button"
                    aria-label={t('ops.detail.bugTicket.useCausal', { key })}
                    className="rounded border border-[var(--color-border)] px-2 py-0.5 text-xs text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]"
                    onClick={() => {
                      setMeta((prev) => ({ ...prev, causalIssueKey: key }));
                      dismissReview('causalTicket');
                    }}
                  >
                    {key}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* セクション本文（各 Backlog セクションフィールド。summary のみ起票必須） */}
          {OPS_BUG_SECTION_FIELDS.map((field) => (
            <div key={field.key} className="space-y-1">
              <label className="block text-sm font-medium text-[var(--color-text-primary)]">
                {t(`ops.detail.bugTicket.sections.${field.key}`)}
                {field.required ? ' *' : ''}
              </label>
              <Textarea
                value={sections[field.key]}
                rows={field.key === 'summary' || field.key === 'cause' ? 4 : 2}
                className="w-full text-xs"
                onChange={(e) => {
                  setSection(field.key, e.target.value);
                }}
              />
              {sectionError(field.key, field.required) && (
                <p className="text-xs text-[var(--color-error)]">{needsHint}</p>
              )}
            </div>
          ))}

          {/* 参考リンク（Backlog description へ） */}
          <div className="space-y-1">
            <label className="block text-sm font-medium text-[var(--color-text-primary)]">
              {t('ops.detail.bugTicket.fields.references')}
            </label>
            <Textarea
              value={references}
              rows={2}
              className="w-full text-xs"
              onChange={(e) => {
                setReferences(e.target.value);
              }}
            />
          </div>

          {create.isError && (
            <p className="text-xs text-[var(--color-error)]">
              {t('ops.detail.bugTicket.createFailed')}
              {create.error instanceof Error && create.error.message !== ''
                ? `: ${create.error.message}`
                : ''}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={onClose}>
              {t('ops.detail.bugTicket.cancel')}
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              disabled={!canProceed || create.isPending}
              onClick={proceed}
            >
              {t('ops.detail.bugTicket.proceed')}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
